import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Search, Download, Edit } from 'lucide-react';
import { residenceAPI } from '@/services/residence';
import { toast } from '@/components/ui/use-toast';

const ResidenceManagementPage = () => {
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  
  // ✅ Edit Dialog State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editForm, setEditForm] = useState({ visaExpiry: '', passportExpiry: '' });

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const res = await residenceAPI.list();
      if (res.success) {
        setEmployees(res.employees);
      } else {
        toast({ title: 'Error', description: 'Failed to load employees', variant: 'destructive' });
      }
    } catch (error) {
      console.error('Load error:', error);
      toast({ title: 'Error', description: 'Failed to load employees', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // ✅ فتح Dialog للتعديل
  const handleEdit = (emp) => {
    setSelectedEmployee(emp);
    setEditForm({
      visaExpiry: emp.visa_expiry || '',
      passportExpiry: emp.passport_expiry || ''
    });
    setEditDialogOpen(true);
  };

  // ✅ حفظ التعديلات
  const handleSaveEdit = async () => {
    try {
      const res = await residenceAPI.update(
        selectedEmployee.id,
        editForm.visaExpiry,
        editForm.passportExpiry
      );

      if (res.success) {
        // تحديث الموظف في الـ state
        setEmployees(prev => prev.map(emp => 
          emp.id === selectedEmployee.id 
            ? { ...emp, visa_expiry: editForm.visaExpiry, passport_expiry: editForm.passportExpiry }
            : emp
        ));
        
        toast({
          title: 'Success ✅',
          description: 'Residence info updated successfully',
          className: 'bg-green-50 border-green-200'
        });
        
        setEditDialogOpen(false);
      } else {
        throw new Error(res.error || 'Update failed');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to update', 
        variant: 'destructive' 
      });
    }
  };

  const getStatus = (dateString) => {
    if (!dateString) return { label: 'Missing', variant: 'destructive' };
    const date = new Date(dateString);
    const today = new Date();
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { label: 'Expired', variant: 'destructive' };
    if (diffDays <= 30) return { label: `Expiring in ${diffDays} days`, variant: 'warning' };
    return { label: 'Valid', variant: 'success' };
  };

  const filteredEmployees = employees.filter(emp => {
    if (searchTerm && !emp.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (filter === 'all') return true;
    
    const visaStatus = getStatus(emp.visa_expiry);
    const passportStatus = getStatus(emp.passport_expiry);
    
    if (filter === 'expired') {
       return visaStatus.label === 'Expired' || passportStatus.label === 'Expired';
    }
    if (filter === 'expiring') {
       return visaStatus.label.includes('Expiring') || passportStatus.label.includes('Expiring');
    }
    return true;
  });

  const exportCSV = () => {
    const headers = ["Name", "Email", "Department", "Visa Expiry", "Passport Expiry"];
    const csvContent = [
      headers.join(','),
      ...filteredEmployees.map(e => `"${e.name}","${e.email}","${e.department || ''}","${e.visa_expiry || ''}","${e.passport_expiry || ''}"`)
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "residence_report.csv";
    link.click();
  };

  return (
    <>
      <Helmet><title>Residence Management - Stone Beam</title></Helmet>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-slate-900">Residence Management</h1>
          <Button variant="outline" onClick={exportCSV}>
            <Download className="w-4 h-4 mr-2" /> Export Report
          </Button>
        </div>

        <div className="flex flex-col md:flex-row gap-4 items-center bg-white p-4 rounded-xl shadow-sm">
           <div className="relative flex-1 w-full">
             <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
             <Input 
               placeholder="Search employee..." 
               value={searchTerm} 
               onChange={e => setSearchTerm(e.target.value)} 
               className="pl-10" 
             />
           </div>
           <div className="flex gap-2">
             <Button 
               variant={filter === 'all' ? 'default' : 'outline'} 
               onClick={() => setFilter('all')}
             >
               All
             </Button>
             <Button 
               variant={filter === 'expiring' ? 'default' : 'outline'} 
               onClick={() => setFilter('expiring')} 
               className="text-yellow-600 border-yellow-200 hover:bg-yellow-50"
             >
               Expiring Soon
             </Button>
             <Button 
               variant={filter === 'expired' ? 'default' : 'outline'} 
               onClick={() => setFilter('expired')} 
               className="text-red-600 border-red-200 hover:bg-red-50"
             >
               Expired
             </Button>
           </div>
        </div>

        <div className="bg-white rounded-xl shadow overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Visa Expiry</TableHead>
                <TableHead>Visa Status</TableHead>
                <TableHead>Passport Expiry</TableHead>
                <TableHead>Passport Status</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : filteredEmployees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No employees found
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmployees.map(emp => {
                  const visa = getStatus(emp.visa_expiry);
                  const passport = getStatus(emp.passport_expiry);
                  return (
                    <TableRow key={emp.id}>
                      <TableCell className="font-medium">{emp.name}</TableCell>
                      <TableCell>{emp.department || '-'}</TableCell>
                      <TableCell>{emp.visa_expiry || '-'}</TableCell>
                      <TableCell><Badge variant={visa.variant}>{visa.label}</Badge></TableCell>
                      <TableCell>{emp.passport_expiry || '-'}</TableCell>
                      <TableCell><Badge variant={passport.variant}>{passport.label}</Badge></TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => handleEdit(emp)}
                          className="h-8"
                        >
                          <Edit className="w-3 h-3 mr-1" /> Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* ✅ Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Residence Information</DialogTitle>
            <DialogDescription>
              Update visa and passport expiry dates for {selectedEmployee?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <label className="text-sm font-medium">Visa Expiry Date</label>
              <Input 
                type="date"
                value={editForm.visaExpiry}
                onChange={e => setEditForm({...editForm, visaExpiry: e.target.value})}
              />
            </div>

            <div className="grid gap-2">
              <label className="text-sm font-medium">Passport Expiry Date</label>
              <Input 
                type="date"
                value={editForm.passportExpiry}
                onChange={e => setEditForm({...editForm, passportExpiry: e.target.value})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setEditDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button 
              type="button" 
              onClick={handleSaveEdit}
              className="bg-safety-orange hover:bg-orange-600"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ResidenceManagementPage;
